import { ClientAppPermission } from '@idc-forms/permissions'
import { Smartphone } from 'lucide-react'
import { useState } from 'react'
import { ClientAccountV2DetailPanel } from '@/features/client-accounts/components/client-account-v2-detail-panel'
import { useClientAccountMutations } from '@/features/client-accounts/hooks/use-client-account-v2-mutations'
import { useClientAccountsByClientQuery } from '@/features/client-accounts/hooks/use-client-accounts-admin-v2-query'
import { QueryErrorState } from '@/shared/components/query-error-state/query-error-state'
import { clientAppLaunchBlockedReason } from '@/shared/config/client-app-launch'
import { usePermissions } from '@/shared/hooks/use-permissions'
import { Button } from '@/shared/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/shared/ui/dialog'
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from '@/shared/ui/empty'
import { Field, FieldDescription, FieldLabel } from '@/shared/ui/field'
import { Input } from '@/shared/ui/input'
import { Skeleton } from '@/shared/ui/skeleton'

/**
 * El acceso a la app móvil, dentro del expediente del cliente.
 *
 * ── POR QUÉ REUSA EL PANEL Y NO DIBUJA LO SUYO ───────────────────────────────
 *
 * Es la MISMA información que la consola de cuentas, sobre la misma entidad. Una segunda
 * implementación se desincroniza en el primer cambio —alguien agrega una acción en un
 * lado y no en el otro— y deja al equipo con dos pantallas que dicen cosas distintas de
 * la misma cuenta.
 *
 * ── POR QUÉ LA CUENTA SE ABRE DESDE ACÁ ──────────────────────────────────────
 *
 * Éste es el lugar donde alguien sabe A QUIÉN se la está abriendo: tiene el expediente
 * delante. La consola global no ofrece «nueva cuenta» a propósito — elegir el cliente de
 * un selector es exactamente cómo se termina invitando a la persona equivocada.
 */
type ClientAppTabProps = {
  clientId: string
  clientName: string
  /** El correo del expediente, si tiene. Se propone como valor inicial, no se impone. */
  clientEmail: string | null
}

export function ClientAppTab({ clientId, clientName, clientEmail }: ClientAppTabProps) {
  const { has } = usePermissions()
  const canManage = has(ClientAppPermission.ManageClientAppAccess)
  const query = useClientAccountsByClientQuery(clientId)
  const { create } = useClientAccountMutations()
  // Abrir la cuenta es lo que después permite invitar: con la app sin publicar el servidor lo
  // rechaza (`409 CLIENT_APP_NOT_LAUNCHED`), así que el botón se ve pero no se puede apretar.
  const launchBlockedReason = clientAppLaunchBlockedReason()
  const [open, setOpen] = useState(false)
  const [email, setEmail] = useState(clientEmail ?? '')

  if (query.isLoading) {
    return (
      <div className="flex flex-col gap-2 p-3">
        <Skeleton className="h-16 w-full rounded-md" />
        <Skeleton className="h-24 w-full rounded-md" />
      </div>
    )
  }

  if (query.isError) {
    return (
      <div className="p-3">
        <QueryErrorState
          error={query.error}
          resource="el acceso a la app"
          onRetry={() => {
            void query.refetch()
          }}
        />
      </div>
    )
  }

  const accounts = query.data?.data ?? []

  // Un cliente puede tener más de una cuenta sólo por un error de carga —el correo es
  // único y la cuenta es 1:1 con la persona—, así que se muestran todas en vez de
  // quedarse con la primera: esconder la segunda es esconder el problema.
  if (accounts.length > 0) {
    return (
      <div className="flex flex-col">
        {accounts.map((account) => (
          <ClientAccountV2DetailPanel key={account.id} accountId={account.id} />
        ))}
      </div>
    )
  }

  return (
    <div className="p-3">
      <Empty className="py-6">
        <EmptyHeader>
          <EmptyMedia>
            <Smartphone className="h-6 w-6 text-muted-foreground" aria-hidden />
          </EmptyMedia>
          <EmptyTitle>Sin cuenta en la aplicación</EmptyTitle>
          <EmptyDescription>
            Este cliente todavía no puede entrar a la aplicación. La cuenta se abre sola al aprobar
            una venta; también puedes abrirla desde aquí.
          </EmptyDescription>
        </EmptyHeader>
        {canManage ? (
          <div className="mt-3 flex flex-col items-center gap-1.5">
            <Button
              type="button"
              size="sm"
              className="gap-1.5"
              disabled={launchBlockedReason !== null}
              onClick={() => {
                setEmail(clientEmail ?? '')
                setOpen(true)
              }}
            >
              <Smartphone className="h-3.5 w-3.5" />
              Abrir cuenta
            </Button>
            {launchBlockedReason ? (
              <p className="text-muted-foreground text-xs">{launchBlockedReason}</p>
            ) : null}
          </div>
        ) : null}
      </Empty>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Abrir cuenta en la aplicación</DialogTitle>
            <DialogDescription>
              {clientName ? `Para ${clientName}. ` : ''}
              Es el correo al que llegará el código para entrar. Abrir la cuenta no le envía nada
              todavía.
            </DialogDescription>
          </DialogHeader>

          <form
            onSubmit={(event) => {
              event.preventDefault()
              const normalized = email.trim().toLowerCase()
              if (!normalized) return
              create.mutate(
                { clientId, email: normalized },
                {
                  onSuccess: () => {
                    setOpen(false)
                  }
                }
              )
            }}
          >
            <Field>
              <FieldLabel htmlFor="client-app-new-email">Correo</FieldLabel>
              <Input
                id="client-app-new-email"
                type="email"
                autoComplete="off"
                value={email}
                onChange={(event) => {
                  setEmail(event.target.value)
                }}
              />
              <FieldDescription>
                {clientEmail
                  ? 'Se propone el correo del expediente. Puedes cambiarlo.'
                  : 'El expediente no tiene correo cargado, así que hay que escribirlo aquí.'}
              </FieldDescription>
            </Field>

            <DialogFooter className="mt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setOpen(false)
                }}
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={email.trim() === '' || create.isPending}>
                {create.isPending ? 'Abriendo…' : 'Abrir cuenta'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  )
}
